import { useEffect } from "react";
import { useLingui } from "@lingui/react/macro";

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void };
  }
}

const TallyForm = ({
  formId: id,
  onSubmission: onSubmit,
  organization,
  siteId,
}: {
  formId: string;
  onSubmission: () => void;
  organization: string;
  siteId: string;
}) => {
  const { t } = useLingui();

  useEffect(() => {
    const widgetScriptSrc = "https://tally.so/widgets/embed.js";

    const load = () => {
      if (window.Tally) {
        window.Tally.loadEmbeds();
        return;
      }
      // Fallback if window.Tally is not available
      document
        .querySelectorAll<HTMLIFrameElement>(
          "iframe[data-tally-src]:not([src])",
        )
        .forEach((iframeEl) => {
          const src = iframeEl.dataset.tallySrc;
          if (src) {
            iframeEl.src = src;
          }
        });
    };

    if (window.Tally) {
      load();
      return;
    }

    if (document.querySelector(`script[src="${widgetScriptSrc}"]`) === null) {
      const script = document.createElement("script");
      script.src = widgetScriptSrc;
      script.onload = load;
      script.onerror = load;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://tally.so") {
        return;
      }
      if (typeof event.data !== "string" || !event.data.includes("Tally.")) {
        return;
      }

      let parsed: { event?: string; payload?: { formId?: string } };
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      if (
        parsed.event === "Tally.FormSubmitted" &&
        parsed.payload?.formId === id
      ) {
        onSubmit();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [id, onSubmit]);

  return (
    <iframe
      data-tally-src={`https://tally.so/embed/${id}?organization=${encodeURIComponent(organization)}&site_id=${encodeURIComponent(siteId)}&alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1`}
      loading="lazy"
      width="100%"
      height={500}
      style={{ border: "none" }}
      title={t`Utvärdering`}
    />
  );
};

export default TallyForm;
